import { useState, useEffect, useCallback } from 'react';
import { API_BASE, ENDPOINTS } from '../config/api';
import { useAuth } from '../context/AuthContext';

export type GuardrailSeverity = 'block' | 'warn';
export type GuardrailAppliesTo = 'input' | 'output' | 'both';

export type GuardrailRule = {
  id: number;
  category: string;
  label: string;
  pattern: string;
  severity: GuardrailSeverity;
  applies_to: GuardrailAppliesTo;
  is_active: boolean;
  created_at: string;
  updated_at: string;
};

export type CreateRulePayload = Omit<GuardrailRule, 'id' | 'created_at' | 'updated_at'>;
export type UpdateRulePayload = Partial<Omit<GuardrailRule, 'id' | 'created_at' | 'updated_at'>>;

export function useGuardrails() {
  const { token } = useAuth();
  const [rules, setRules] = useState<GuardrailRule[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const authHeaders = useCallback((): Record<string, string> => {
    const h: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) h.Authorization = `Bearer ${token}`;
    return h;
  }, [token]);

  const apiFetch = useCallback(async <T>(path: string, init?: RequestInit): Promise<T> => {
    const res = await fetch(`${API_BASE}${path}`, {
      headers: authHeaders(),
      ...init,
    });
    if (!res.ok) {
      const text = await res.text().catch(() => res.statusText);
      throw new Error(`${res.status}: ${text}`);
    }
    if (res.status === 204) return undefined as T;
    return res.json() as Promise<T>;
  }, [authHeaders]);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiFetch<{ rules: GuardrailRule[] }>(ENDPOINTS.guardrails);
      setRules(data.rules);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [apiFetch]);

  useEffect(() => { void refresh(); }, [refresh]);

  const createRule = useCallback(async (payload: CreateRulePayload): Promise<GuardrailRule> => {
    const rule = await apiFetch<GuardrailRule>(ENDPOINTS.guardrails, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    setRules((prev) => [...prev, rule]);
    return rule;
  }, [apiFetch]);

  const updateRule = useCallback(async (id: number, payload: UpdateRulePayload): Promise<GuardrailRule> => {
    const rule = await apiFetch<GuardrailRule>(ENDPOINTS.guardrail(id), {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
    setRules((prev) => prev.map((r) => (r.id === id ? rule : r)));
    return rule;
  }, [apiFetch]);

  const deleteRule = useCallback(async (id: number): Promise<void> => {
    await apiFetch<void>(ENDPOINTS.guardrail(id), { method: 'DELETE' });
    setRules((prev) => prev.filter((r) => r.id !== id));
  }, [apiFetch]);

  const toggleRule = useCallback(
    (id: number, is_active: boolean) => updateRule(id, { is_active }),
    [updateRule],
  );

  return { rules, loading, error, refresh, createRule, updateRule, deleteRule, toggleRule };
}
